import React, { useMemo, useState } from "react";
import { Clock, LogIn, LogOut, Users } from "lucide-react";

import { useCompanyUsers } from "../../hooks/useCompanyUsers";
import { hoursBetween, useTimeCard } from "../../hooks/useTimeCard";

/**
 * Time Card (web) — core clock-in/out + current-period hours, plus a manager
 * roster of who's on the clock. Ports the daily-use core of the mobile
 * clockin.tsx screen. Fringe (geofence, PDF export, edit-request approvals)
 * lands in a follow-up.
 */
function fmtHours(h: number): string {
  // Round to whole minutes first so we can never render e.g. "1h 60m".
  const totalMins = Math.round(h * 60);
  return `${Math.floor(totalMins / 60)}h ${totalMins % 60}m`;
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

const TimeCard: React.FC = () => {
  const {
    companyId,
    isManager,
    locations,
    period,
    openPunch,
    periodHours,
    openPunches,
    isLoading,
    clockIn,
    clockOut,
  } = useTimeCard();
  const { data: companyUsersResult } = useCompanyUsers(companyId);

  const [locationId, setLocationId] = useState<string>("");
  const activeLocation = locationId || locations[0]?.id || "";

  const nameFor = useMemo(() => {
    const map = new Map<string, string>();
    (companyUsersResult?.users ?? []).forEach((u) => {
      if (u.user_id)
        map.set(
          u.user_id,
          u.user_name ||
            `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() ||
            "Staff",
        );
    });
    return (id: string) => map.get(id) ?? "Staff";
  }, [companyUsersResult]);

  const periodLabel = `${period.start.toLocaleDateString([], { month: "short", day: "numeric" })} – ${new Date(
    period.end.getTime() - 1,
  ).toLocaleDateString([], { month: "short", day: "numeric" })}`;

  return (
    <div className="mx-auto max-w-3xl space-y-8 px-4 py-8">
      <header className="flex items-center gap-3">
        <Clock className="h-7 w-7 text-rollops-accent" />
        <div>
          <h1 className="text-2xl font-heading font-bold text-text-primary">
            Time Card
          </h1>
          <p className="text-sm text-text-muted">Pay period {periodLabel}</p>
        </div>
      </header>

      {/* Clock in/out */}
      <section className="rounded-xl border border-[#333333] bg-[#221F1F] p-6">
        {openPunch ? (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-text-muted">On the clock since</p>
              <p className="text-xl font-semibold text-text-primary">
                {fmtTime(openPunch.clock_in)}
              </p>
              <p className="text-sm text-rollops-accent">
                {fmtHours(hoursBetween(openPunch.clock_in, null))} elapsed
              </p>
            </div>
            <button
              onClick={() => clockOut.mutate(openPunch.id)}
              disabled={clockOut.isPending}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-rollops-primary px-6 py-3 font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
            >
              <LogOut className="h-5 w-5" />
              {clockOut.isPending ? "Clocking out…" : "Clock out"}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex-1">
              <label className="mb-1 block text-sm text-text-muted">
                Location
              </label>
              <select
                value={activeLocation}
                onChange={(e) => setLocationId(e.target.value)}
                className="w-full rounded-lg border border-[#333333] bg-[#111010] px-3 py-2 text-text-primary sm:max-w-xs"
              >
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={() => activeLocation && clockIn.mutate(activeLocation)}
              disabled={clockIn.isPending || !activeLocation}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-rollops-primary px-6 py-3 font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
            >
              <LogIn className="h-5 w-5" />
              {clockIn.isPending ? "Clocking in…" : "Clock in"}
            </button>
          </div>
        )}
        {(clockIn.isError || clockOut.isError) && (
          <p className="mt-3 text-sm text-rollops-accent">
            Something went wrong — please try again.
          </p>
        )}
      </section>

      {/* This period's hours */}
      <section className="rounded-xl border border-[#333333] bg-[#221F1F] p-6">
        <div className="flex items-baseline justify-between">
          <h2 className="font-heading font-semibold text-text-primary">
            Your hours this period
          </h2>
          <span className="text-2xl font-bold tabular-nums text-text-primary">
            {fmtHours(periodHours)}
          </span>
        </div>
      </section>

      {/* Manager: who's on the clock */}
      {isManager && (
        <section className="rounded-xl border border-[#333333] bg-[#221F1F] p-6">
          <div className="mb-4 flex items-center gap-2">
            <Users className="h-5 w-5 text-rollops-accent" />
            <h2 className="font-heading font-semibold text-text-primary">
              On the clock now
            </h2>
            <span className="rounded-full bg-[#111010] px-2 py-0.5 text-xs text-text-muted">
              {openPunches.length}
            </span>
          </div>
          {openPunches.length === 0 ? (
            <p className="text-sm text-text-muted">
              No one is clocked in right now.
            </p>
          ) : (
            <ul className="divide-y divide-[#333333]">
              {openPunches.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center justify-between py-2"
                >
                  <span className="text-text-primary">
                    {nameFor(p.user_id)}
                  </span>
                  <span className="text-sm text-text-muted">
                    since {fmtTime(p.clock_in)} ·{" "}
                    {fmtHours(hoursBetween(p.clock_in, null))}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {isLoading && (
        <p className="text-center text-sm text-text-muted">Loading…</p>
      )}
    </div>
  );
};

export default TimeCard;
